import React, { useMemo, useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import { FormPageShell } from "../../common/dashboard/FormPageShell";
import {
  FormSelectField,
  FormTextField,
} from "../../common/dashboard/FormFields";
import { SectionShell } from "../../common/dashboard/SectionShell";
import {
  wizardGoldButtonSx,
  wizardOutlinedButtonSx,
} from "../../common/dashboard/formStyles";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import { provinceOptions } from "../../common/utilities/ProvincePicker";
import {
  getProspectErrorMessage,
  ProspectFieldsInput,
  ProspectFormErrors,
  ProspectFormValues,
  ProspectSaveResult,
  prospectFormValuesMatch,
  toProspectFieldsInput,
  validateProspectForm,
} from "./prospectFormModel";
import {
  LeadCaptureSelect,
  ProspectLeadCaptureOption,
} from "./LeadCaptureSelect";

export type ProspectFormAction = "save" | "convert";

type ProspectFormProps = {
  mode: "create" | "update";
  initialValues: ProspectFormValues;
  leadCaptures: ProspectLeadCaptureOption[];
  leadCapturesLoading?: boolean;
  leadCapturesError?: string;
  cancelPath: string;
  onCreateLeadCapture: (label: string) => Promise<ProspectLeadCaptureOption>;
  onSave: (input: ProspectFieldsInput) => Promise<ProspectSaveResult>;
  onSaved?: (
    prospect: ProspectSaveResult,
    action: ProspectFormAction,
  ) => void | Promise<void>;
};

type StatusMessage = {
  severity: "success" | "error";
  message: string;
};

const scrollToFirstError = () => {
  window.setTimeout(() => {
    document
      .querySelector('[data-prospect-field-error="true"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 60);
};

type FieldFrameProps = {
  error?: string;
  children: React.ReactNode;
};

const FieldFrame = ({ error, children }: FieldFrameProps) => (
  <div data-prospect-field-error={error ? "true" : undefined}>{children}</div>
);

export const ProspectForm = ({
  mode,
  initialValues,
  leadCaptures,
  leadCapturesLoading = false,
  leadCapturesError,
  cancelPath,
  onCreateLeadCapture,
  onSave,
  onSaved,
}: ProspectFormProps) => {
  const [values, setValues] = useState<ProspectFormValues>(initialValues);
  const [savedValues, setSavedValues] =
    useState<ProspectFormValues>(initialValues);
  const [errors, setErrors] = useState<ProspectFormErrors>({});
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null,
  );
  const [footerErrorMessage, setFooterErrorMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<ProspectFormAction | null>(
    null,
  );

  const isDirty = useMemo(
    () => !prospectFormValuesMatch(values, savedValues),
    [savedValues, values],
  );
  const isSaving = pendingAction !== null;

  const updateField = (field: keyof ProspectFormValues, value: string) => {
    setValues((currentValues) => ({ ...currentValues, [field]: value }));
    setErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    if (statusMessage) {
      setStatusMessage(null);
    }
    if (footerErrorMessage) {
      setFooterErrorMessage("");
    }
  };

  const submit = async (action: ProspectFormAction) => {
    const nextErrors = validateProspectForm(values);
    setErrors(nextErrors);
    setStatusMessage(null);
    setFooterErrorMessage("");

    if (Object.keys(nextErrors).length > 0) {
      setFooterErrorMessage(
        "Please review the highlighted fields before saving.",
      );
      scrollToFirstError();
      return;
    }

    setPendingAction(action);
    try {
      const savedProspect = await onSave(toProspectFieldsInput(values));
      setSavedValues(values);
      setFooterErrorMessage("");
      setStatusMessage({
        severity: "success",
        message:
          mode === "create"
            ? "Prospect saved successfully."
            : "Prospect changes saved successfully.",
      });
      await onSaved?.(savedProspect, action);
    } catch (saveError) {
      setFooterErrorMessage(
        getProspectErrorMessage(saveError, "The prospect could not be saved."),
      );
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit("save");
      }}
    >
      <FormPageShell
        headingId={`${mode}-prospect-form-heading`}
        heading={mode === "create" ? "Create prospect" : "Update prospect"}
        beforeMain={<UnsavedChangesPrompt when={isDirty} />}
        alerts={
          statusMessage || leadCapturesError ? (
            <div className="mb-6 space-y-3">
              {statusMessage ? (
                <Alert severity={statusMessage.severity}>
                  {statusMessage.message}
                </Alert>
              ) : null}
              {leadCapturesError ? (
                <Alert severity="error">{leadCapturesError}</Alert>
              ) : null}
            </div>
          ) : undefined
        }
        actionBarAlert={
          footerErrorMessage ? (
            <Alert
              severity="error"
              className="mb-4"
              onClose={() => setFooterErrorMessage("")}
            >
              {footerErrorMessage}
            </Alert>
          ) : undefined
        }
        actionBar={
          <>
            <Button
              component={Link}
              to={cancelPath}
              variant="outlined"
              disabled={isSaving}
              sx={{ ...wizardOutlinedButtonSx, minWidth: 108 }}
            >
              Cancel
            </Button>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                startIcon={
                  pendingAction === "save" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                sx={{ ...wizardGoldButtonSx, minWidth: 150 }}
              >
                Save Prospect
              </Button>
              {mode === "update" ? (
                <Button
                  type="button"
                  variant="contained"
                  disabled={isSaving}
                  onClick={() => void submit("convert")}
                  startIcon={
                    pendingAction === "convert" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  sx={{ ...wizardGoldButtonSx, minWidth: 170 }}
                >
                  Convert to Member
                </Button>
              ) : null}
            </div>
          </>
        }
      >
        <div className="max-w-[570px] space-y-5">
          <FieldFrame error={errors.leadCaptureId}>
            <LeadCaptureSelect
              value={values.leadCaptureId}
              onChange={(value) => updateField("leadCaptureId", value)}
              options={leadCaptures}
              onCreateLeadCapture={onCreateLeadCapture}
              error={errors.leadCaptureId}
              loading={leadCapturesLoading}
              disabled={isSaving}
            />
          </FieldFrame>
          <FieldFrame error={errors.createdByName}>
            <FormTextField
              id="prospect-created-by"
              label="Created By"
              value={values.createdByName}
              onChange={(value) => updateField("createdByName", value)}
              error={errors.createdByName}
              placeholder="Enter Staff Name"
              disabled={isSaving}
              containerClassName="mb-0 min-w-0"
            />
          </FieldFrame>
        </div>

        <SectionShell
          id="prospect-company-information"
          title="Company Information"
          variant="card"
        >
          <div className="space-y-5">
            <FieldFrame error={errors.companyName}>
              <FormTextField
                id="prospect-company-name"
                label="Company Name"
                value={values.companyName}
                onChange={(value) => updateField("companyName", value)}
                error={errors.companyName}
                placeholder="Enter Company Name"
                required
                disabled={isSaving}
                containerClassName="mb-0 min-w-0"
              />
            </FieldFrame>

            <div className="max-w-[570px]">
              <FieldFrame error={errors.companyWebsite}>
                <FormTextField
                  id="prospect-company-website"
                  label="Company Website"
                  value={values.companyWebsite}
                  onChange={(value) => updateField("companyWebsite", value)}
                  error={errors.companyWebsite}
                  placeholder="Enter Company Website URL"
                  type="url"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-[1fr_1fr_0.75fr_0.28fr]">
              <FieldFrame error={errors.mainContactName}>
                <FormTextField
                  id="prospect-main-contact-name"
                  label="Main Contact Name"
                  value={values.mainContactName}
                  onChange={(value) => updateField("mainContactName", value)}
                  error={errors.mainContactName}
                  placeholder="Enter Main Contact Name"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.mainContactEmail}>
                <FormTextField
                  id="prospect-main-contact-email"
                  label="Main Contact Email"
                  value={values.mainContactEmail}
                  onChange={(value) => updateField("mainContactEmail", value)}
                  error={errors.mainContactEmail}
                  placeholder="Enter Company Contact Email"
                  type="email"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.mainPhoneNumber}>
                <FormTextField
                  id="prospect-main-phone"
                  label="Main Phone"
                  value={values.mainPhoneNumber}
                  onChange={(value) => updateField("mainPhoneNumber", value)}
                  error={errors.mainPhoneNumber}
                  placeholder="Enter Company Phone"
                  type="tel"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.mainPhoneNumberExtension}>
                <FormTextField
                  id="prospect-main-phone-extension"
                  label="Ext."
                  value={values.mainPhoneNumberExtension}
                  onChange={(value) =>
                    updateField("mainPhoneNumberExtension", value)
                  }
                  error={errors.mainPhoneNumberExtension}
                  placeholder="0000"
                  type="tel"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
            </div>
          </div>
        </SectionShell>

        <SectionShell
          id="prospect-company-address"
          title="Company Address"
          variant="card"
        >
          <div className="space-y-5">
            <FieldFrame error={errors.streetAddress}>
              <FormTextField
                id="prospect-street-address"
                label="Street Address"
                value={values.streetAddress}
                onChange={(value) => updateField("streetAddress", value)}
                error={errors.streetAddress}
                placeholder="Enter Street Address, Example: 123 Maple E.g."
                disabled={isSaving}
                containerClassName="mb-0 min-w-0"
              />
            </FieldFrame>

            <div className="grid gap-5 md:grid-cols-2">
              <FieldFrame error={errors.city}>
                <FormTextField
                  id="prospect-city"
                  label="City"
                  value={values.city}
                  onChange={(value) => updateField("city", value)}
                  error={errors.city}
                  placeholder="Enter City Name"
                  required
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.province}>
                <FormSelectField
                  id="prospect-province"
                  label="Province/State"
                  value={values.province}
                  onChange={(value) => updateField("province", value)}
                  options={provinceOptions}
                  placeholder="Select Province/State"
                  error={errors.province}
                  required
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.postalCode}>
                <FormTextField
                  id="prospect-postal-code"
                  label="Postal/Zip Code"
                  value={values.postalCode}
                  onChange={(value) => updateField("postalCode", value)}
                  error={errors.postalCode}
                  placeholder="Enter the Postal/Zip Code"
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
              <FieldFrame error={errors.country}>
                <FormTextField
                  id="prospect-country"
                  label="Country"
                  value={values.country}
                  onChange={(value) => updateField("country", value)}
                  error={errors.country}
                  placeholder="Enter Country Name"
                  required
                  disabled={isSaving}
                  containerClassName="mb-0 min-w-0"
                />
              </FieldFrame>
            </div>
          </div>
        </SectionShell>
      </FormPageShell>
    </form>
  );
};
